import { Heading, SegmentedTabs } from "@overmap-ai/blocks"
import { type FormRevision, type Stored, EMPTY_ARRAY } from "@overmap-ai/common"
import {
	selectAttachmentsOfFormRevision,
	selectFormById,
	selectFormRevisionsOfForm,
	selectLatestFormRevisionOfForm,
} from "@overmap-ai/core"
import { FieldSchema, type FieldSchemaConfig, FormBuilder, getImagesFromFields } from "@overmap-ai/forms"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { FieldsRevisionsTimeline } from "../../components/FieldsRevisionsTimeline/FieldsRevisionsTimeline"
import { useDeserializeFieldSchemaConfig, useHydrateFieldsWithAttachments } from "../../components/Form/hooks"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/forms/$formId/fields")

export const FormSettingsFieldsView = memo(() => {
	const { formId } = routeApi.useParams()

	const form = useAppSelector(selectFormById(formId))

	if (!form) {
		throw new Error("Expected form to exist.")
	}

	const formRevisions = useAppSelector(selectFormRevisionsOfForm(form.uuid))
	const latestFormRevision = useAppSelector(selectLatestFormRevisionOfForm(form.uuid))
	const navigate = useNavigate()
	const attachmentsOfRevision = useShallowEqaulSelector(
		selectAttachmentsOfFormRevision(latestFormRevision?.uuid ?? ""),
	)

	const handleFormRevisionSelect = useCallback(
		(formRevision: Stored<FormRevision>) => {
			void navigate({
				from: "/$organizationId/settings/forms/$formId/fields",
				to: "/$organizationId/settings/forms/$formId/revisions/$formRevisionId",
				params: (previousParams) => ({ ...previousParams, formRevisionId: formRevision.uuid }),
			})
		},
		[navigate],
	)

	const handleCancel = useCallback(() => {
		void navigate({
			from: "/$organizationId/settings/forms/$formId/fields",
			to: "/$organizationId/settings/forms/$formId",
		})
	}, [navigate])

	const handleFormBuilderSave = useCallback(
		async (fields: FieldSchemaConfig) => {
			const images = await getImagesFromFields(fields)

			const [offlineRevision, _promise] = sdk.formRevisions.add({
				form: form.uuid,
				fields: FieldSchema.serializeConfig(fields),
			})

			if (Object.keys(images).length !== 0) {
				void sdk.formRevisionAttachments.bulkAddForFormRevision(offlineRevision.uuid, images)
			}
		},
		[form.uuid],
	)

	const fieldSchemaConfig = useDeserializeFieldSchemaConfig(latestFormRevision?.fields ?? EMPTY_ARRAY)

	const fieldsWithImages = useHydrateFieldsWithAttachments(
		fieldSchemaConfig,
		attachmentsOfRevision,
		sdk.formRevisionAttachments,
	)

	return (
		<ViewContainer>
			<section className="flex size-full flex-col gap-4">
				<Heading size="xl">Fields</Heading>

				<FormBuilder.Root
					initialFields={fieldsWithImages}
					onCancel={handleCancel}
					onSave={handleFormBuilderSave}
				>
					<SegmentedTabs.Root defaultValue="edit" className="flex size-full flex-col gap-4 overflow-hidden">
						<SegmentedTabs.List className="flex" accentColor="base" size="sm" variant="surface">
							<SegmentedTabs.Trigger className="grow basis-1" value="edit">
								List
							</SegmentedTabs.Trigger>
							<SegmentedTabs.Trigger className="grow basis-1" value="flow">
								Flow
							</SegmentedTabs.Trigger>
							<SegmentedTabs.Trigger className="grow basis-1" value="preview">
								Preview
							</SegmentedTabs.Trigger>

							<SegmentedTabs.Trigger className="grow basis-1" value="history">
								History
							</SegmentedTabs.Trigger>
						</SegmentedTabs.List>

						<SegmentedTabs.Content value="edit" className="size-full overflow-y-auto overflow-x-clip">
							<FormBuilder.ListBuilder />
						</SegmentedTabs.Content>

						<SegmentedTabs.Content value="flow" className="size-full overflow-y-auto overflow-x-clip">
							<FormBuilder.FlowBuilder />
						</SegmentedTabs.Content>

						<SegmentedTabs.Content value="preview" className="size-full overflow-y-auto overflow-x-clip">
							<FormBuilder.Preview />
						</SegmentedTabs.Content>

						<SegmentedTabs.Content value="history" className="size-full overflow-y-auto overflow-x-clip">
							<FieldsRevisionsTimeline
								fieldsRevisions={formRevisions}
								onRevisionSelect={handleFormRevisionSelect}
							/>
						</SegmentedTabs.Content>
					</SegmentedTabs.Root>
				</FormBuilder.Root>
			</section>
		</ViewContainer>
	)
})

FormSettingsFieldsView.displayName = "FormSettingsFieldsView"
